import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { tokenAtom } from "../atoms/auth/tokenAtom";
import { useProductCart } from "../atoms/products/hooks/useProductCart";
import useRevokeTokenMutation from "../hooks/chatHooks/useRevokeTokenMutation";
import { SCREENS } from "../routes/screens";
import { emitAnalyticsTrackingEvent } from "../utils/analyticsUtils";
import type { AppDispatch, RootState } from "./";
import { logout } from "./actions/accountInfo";
import { fetchProfile } from "./actions/users";
import { FileVersion } from "./models/fileVersion";
import { resetPhoneNumberAuthAtom } from "../stories/components/Auth/atoms";

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useLogout = (screenToNavigateTo?: SCREENS) => {
  const { mutateAsync: revokeTokenAsync } = useRevokeTokenMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.accountInfo.user);
  const { instance } = useMsal();
  const isUmgAuthenticated = useIsAuthenticated();
  const setToken = useSetAtom(tokenAtom);
  const setResetPhoneNumberAuthAtom = useSetAtom(resetPhoneNumberAuthAtom);
  const { handleClearCart } = useProductCart();

  return useCallback(async () => {
    if (!user) return;
    emitAnalyticsTrackingEvent("clicked_log_out", {});
    if (isUmgAuthenticated) {
      try {
        await instance.logoutPopup();
      } catch (e) {
        return toast.error("Error logging out of UMG");
      }
    }
    await revokeTokenAsync();
    setToken("");
    dispatch(logout());
    handleClearCart();
    setResetPhoneNumberAuthAtom();
    if (screenToNavigateTo) navigate(screenToNavigateTo);
  }, [
    user,
    isUmgAuthenticated,
    instance,
    dispatch,
    navigate,
    revokeTokenAsync,
    setToken,
    setResetPhoneNumberAuthAtom,
    screenToNavigateTo,
    handleClearCart,
  ]);
};

export const useUser = (userId: number | undefined) => {
  const users = useAppSelector((state) => state.usersService);
  const user = users[userId ?? -1];
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (user) {
      return;
    }

    if (!userId) {
      return;
    }
    void dispatch(fetchProfile({ user_id: userId }));
  }, [user, dispatch, userId]);

  return user;
};

export const useIsFileUploadedByUser = (file: FileVersion) => {
  const user = useAppSelector((state) => state.accountInfo.user);
  return useMemo(() => {
    if (!user) return false;
    return user.id === file.created_by_user?.id;
  }, [file, user]);
};

export const useProjectFromProjectMap = (projectId: number) => {
  const projects = useAppSelector((state) => state.projectsMapStore.projects);
  return projects[projectId];
};
